import React, { useCallback, useMemo, useState } from 'react';
import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { useMessageQueue } from '@erickson/components';
import { ExportJob } from '@/API';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Tooltip } from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import { applyDefaultFilterOperators } from '@/utils/filter.ts';
import { ServiceDataGrid } from '@/components/ServiceDataGrid';
import { useServices } from '@/components/ServiceContext.tsx';
import { useNavigate } from 'react-router-dom';
import { ExportPreviewPageState } from '@/pages/ExportPreviewPage/ExportPreviewPage.tsx';
import { FilterSelections, GraphQLQuery } from '@lambda-layer/types/filter';
import JobDetailDialog from '@/components/ExportJobTable/JobDetailDialog.tsx';
import log from 'loglevel';
import { ListType } from '@/service/ExportJobService.ts';
import { downloadFileFromS3 } from '@/utils/downloadUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import { isAggregateDefinition } from '@lambda-layer/utils/commonUtils';

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'COMPLETE':
      return <CheckCircleIcon style={{ color: 'green' }} />;
    case 'FAILED':
      return <ErrorIcon style={{ color: 'red' }} />;
    default:
      return <HourglassEmptyIcon style={{ color: 'orange' }} />;
  }
};

// A helper function to get style by format
const getFormatStyle = (format?: string) => {
  switch (format) {
    case 'xlsx':
      return {
        color: '#4caf50', // green
        label: 'XLSX',
      };
    case 'csv':
      return {
        color: '#2196f3', // blue
        label: 'CSV',
      };
    default:
      return {
        color: '#757575', // grey if format unknown
        label: (format ?? '').toUpperCase(),
      };
  }
};

const ExportJobTable: React.FC<{
  listType: ListType;
  reportId?: string;
  filterDefinitions: FilterDefinition[];
  onApplyUserFilter?: (filterSelections: FilterSelections) => void;
}> = ({ listType, reportId, filterDefinitions, onApplyUserFilter }) => {
  const navigate = useNavigate();
  const messageQueue = useMessageQueue();
  const { exportJobService, routeService } = useServices();

  const [selectedJob, setSelectedJob] = useState<ExportJob | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);

  const isAggregate = isAggregateDefinition(filterDefinitions);

  const handleOpenModal = useCallback((job: ExportJob) => {
    setSelectedJob(job);
    setModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const deleteJob = useCallback(
    async (job: ExportJob) => {
      try {
        await exportJobService.delete(job.id);
        messageQueue.addMessage({
          text: `Scheduled deletion of job ${job.exportName}`,
          severity: 'success',
        });
      } catch (error) {
        messageQueue.addMessage({
          text: `Error deleting job ${job.exportName}`,
          severity: 'error',
        });
        log.error('Error deleting job:', error);
      }
    },
    [messageQueue, exportJobService]
  );

  const downloadJobFile = useCallback(
    async (
      name: string,
      s3Key?: string | null,
      outputFormat?: string | null
    ) => {
      try {
        await downloadFileFromS3(name, s3Key || '', outputFormat || undefined);
      } catch (error) {
        messageQueue.addMessage({
          text: `Error downloading job file`,
          severity: 'error',
        });
        log.error('Error downloading job file:', error);
      }
    },
    [messageQueue]
  );

  const handleQueryUpdated = React.useCallback(
    (query: GraphQLQuery<GridValidRowModel>) => {
      query.listType = { eq: listType };
      if (reportId) {
        query.reportId = { eq: reportId };
      } else {
        query.reportId = { exists: false };
      }
      return query;
    },
    [listType, reportId]
  );

  const handleNavigateToExportPreviewPage = useCallback(
    async (exportJob: ExportJob) => {
      // Export preview page should be the current path + '/export-preview'
      navigate(
        routeService.routes?.find(
          r => r.path === `${location.pathname}/export-preview`
        )?.path || '/',
        {
          state: {
            exportJob,
          } as ExportPreviewPageState,
          viewTransition: true,
        }
      );
    },
    [navigate, routeService]
  );

  /**
   * Filter out rows on subscription updates
   */
  const rowFilter = useCallback(
    (row: GridValidRowModel) => {
      // Check listType
      if (row.listType !== listType) {
        return false;
      }

      // If we have a reportId in the query, enforce it
      if (reportId && row.reportId !== reportId) {
        return false;
      }

      // If no reportId was queried, exclude rows that have a reportId
      return !(!reportId && row.reportId);
    },
    [listType, reportId]
  );

  const columns: GridColDef<ExportJob>[] = useMemo(
    () =>
      applyDefaultFilterOperators([
        {
          field: '',
          headerName: 'Actions',
          filterable: false,
          width: 175,
          renderCell: params => {
            const job = params.row as ExportJob;
            return (
              <Box display="flex" alignItems="center" height="100%">
                <Box>
                  <Tooltip title="View Details">
                    <IconButton onClick={() => handleOpenModal(job)}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                {!isAggregate && (
                  <Tooltip title="Preview">
                    <Box>
                      <IconButton
                        aria-label={`Preview ${job.exportName}`}
                        disabled={job.status === 'FAILED'}
                        onClick={() => handleNavigateToExportPreviewPage(job)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                <Tooltip title="Download">
                  <Box>
                    <IconButton
                      aria-label={`Download ${job.exportName}`}
                      disabled={!job.s3Key}
                      onClick={() =>
                        downloadJobFile(
                          job.exportName,
                          job.s3Key,
                          job.outputFormat
                        )
                      }
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
                <Tooltip title="Delete">
                  <Box>
                    <IconButton
                      aria-label={`Delete ${job.exportName}`}
                      onClick={() => deleteJob(job)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
              </Box>
            );
          },
        },
        {
          field: 'exportName',
          headerName: 'Export Job',
          width: 300,
          flex: 1,
          renderCell: (params: GridRenderCellParams<ExportJob>) => {
            const job = params.row;
            const { color, label } = getFormatStyle(
              job.outputFormat || undefined
            );

            return (
              <Box display="flex" alignItems="center" gap={1}>
                <span>{job.exportName}</span>
                {job.outputFormat && (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: color,
                      borderRadius: '2px',
                      padding: '0 2px',
                      fontSize: '0.55rem',
                      color: color,
                      display: 'inline-block',
                      lineHeight: '12px',
                    }}
                  >
                    {label}
                  </Box>
                )}
              </Box>
            );
          },
        },
        {
          field: 'owner',
          headerName: 'User',
          width: 125,
          flex: 0.5,
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 60,
          renderCell: (params: GridRenderCellParams<ExportJob>) => (
            <Box display="flex" alignItems="center" height="100%">
              <Tooltip
                aria-label={`Status: ${params.row.status}`}
                title={params.row.statusMessage || ''}
              >
                {getStatusIcon(params.row.status)}
              </Tooltip>
            </Box>
          ),
        },
        {
          field: 'createdAt',
          headerName: 'Export Date',
          width: 175,
          renderCell: (params: GridRenderCellParams<ExportJob>) => {
            const date = new Date(params.value);
            const formattedDate = format(date, 'yyyy-MM-dd HH:mm:ss');
            const relativeTime = formatDistanceToNow(date, { addSuffix: true });

            return (
              <Tooltip title={formattedDate}>
                <span>{relativeTime}</span>
              </Tooltip>
            );
          },
        },
      ]),
    [
      isAggregate,
      handleOpenModal,
      handleNavigateToExportPreviewPage,
      downloadJobFile,
      deleteJob,
    ]
  );

  return (
    <>
      {selectedJob && (
        <JobDetailDialog
          filterDefinitions={filterDefinitions}
          job={selectedJob}
          open={isModalOpen}
          onClose={handleCloseModal}
          onApplyUserFilter={
            onApplyUserFilter
              ? (filterSelections: FilterSelections) => {
                  handleCloseModal();
                  // TODO: message?
                  onApplyUserFilter(filterSelections);
                }
              : undefined
          }
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <ServiceDataGrid
          rowSelection={false}
          service={exportJobService}
          columns={columns}
          pageSize={10}
          sortModel={[{ field: 'createdAt', sort: 'desc' }]}
          onQueryUpdated={handleQueryUpdated}
          rowFilter={rowFilter}
          quickSearchFields={['exportName', 'owner', 'status']}
        />
      </div>
    </>
  );
};

export default ExportJobTable;
